(function () {
  'use strict';

  const grid = document.getElementById('WishlistGrid');
  if (!grid) return;

  // --- Config ---
  const CARD_VIEW = 'wishlist-card';
  const POLL_MS = 200;
  const STABLE_TICKS = 2;
  const MAX_WAIT_ITEMS_MS = 12000;
  const MAX_WAIT_ITEMS_SOFT_MS = 2500;
  const FETCH_TIMEOUT_MS = 10000;
  const FETCH_CONCURRENCY = 6;

  const EMPTY_GRACE_INITIAL_MS = 900;
  const EMPTY_GRACE_PAGE_MS = 3500;
  const EMPTY_GRACE_SOFT_MS = 500;

  const TEXT = {
    loading: 'Laster ønskelisten…',
    error: 'Kunne ikke laste ønskelisten. Prøv igjen.'
  };

  const LOADING_LI_HTML =
    `<li class="wishlist-status-item"><p class="wishlist-status" aria-live="polite">${TEXT.loading}</p></li>`;
  const ERROR_LI_HTML =
    `<li class="wishlist-status-item"><p class="wishlist-status" aria-live="polite">${TEXT.error}</p></li>`;

  // --- State ---
  let xbRoot = null;

  let currentJob = 0;
  let jobAbort = null;

  let debounceTimer = null;
  let pendingMode = null;

  let suppressObserverUntil = 0;

  const cardCache = new Map(); // key: handle|variantId
  const pendingRemovals = new Set(); // handles expected to disappear

  function now() {
    return Date.now();
  }

  function delay(ms) {
    return new Promise((r) => setTimeout(r, ms));
  }

  function setLoading(on, mode) {
    grid.dataset.loading = on ? '1' : '0';

    if (on) {
      // Show loading only if there are no cards yet (initial/page)
      if (mode === 'initial' || mode === 'page') {
        const hasCards = !!grid.querySelector('li:not(.wishlist-status-item)');
        if (!hasCards) grid.innerHTML = LOADING_LI_HTML;
      }
      return;
    }

    const status = grid.querySelector('.wishlist-status-item');
    if (status) status.remove();
  }

  function paintStatus(html) {
    grid.innerHTML = html;
  }

  function extractHandleFromHref(href) {
    const m = (href || '').match(/\/products\/([^\/\?]+)/);
    return m ? m[1] : null;
  }

  function parseItems(root) {
    const map = new Map(); // handle -> variantId

    const nodes = root.querySelectorAll('.xb-wishlist-product__item');
    nodes.forEach((node) => {
      const a = node.querySelector('a.xb-wishlist-product__link[href*="/products/"]');
      if (!a) return;

      const handle = extractHandleFromHref(a.getAttribute('href') || '');
      if (!handle) return;

      const sel = node.querySelector('select.xb-wishlist-product__select');
      const variantId = sel && sel.value ? String(sel.value) : '';

      map.set(handle, variantId);
    });

    return Array.from(map, ([handle, variantId]) => ({ handle, variantId }));
  }

  function xbHasEmptyState(root) {
    return !!root.querySelector('.xb-wishlist-products__no-product');
  }

  function findXbItemByHandle(handle) {
    if (!xbRoot) return null;

    const nodes = xbRoot.querySelectorAll('.xb-wishlist-product__item');
    for (const node of nodes) {
      const a = node.querySelector('a.xb-wishlist-product__link[href*="/products/"]');
      if (!a) continue;

      const h = extractHandleFromHref(a.getAttribute('href') || '');
      if (h === handle) return node;
    }

    return null;
  }

  function findXbItemsByHandle(handle) {
    if (!xbRoot) return [];
    const out = [];

    const nodes = xbRoot.querySelectorAll('.xb-wishlist-product__item');
    for (const node of nodes) {
      const a = node.querySelector('a.xb-wishlist-product__link[href*="/products/"]');
      if (!a) continue;

      const h = extractHandleFromHref(a.getAttribute('href') || '');
      if (h === handle) out.push(node);
    }

    return out;
  }

  function clickXbRemove(handle) {
    let xbItem = findXbItemByHandle(handle);

    // Very rare fallback: if DOM is in transition and exact lookup failed
    if (!xbItem) {
      const candidates = findXbItemsByHandle(handle);
      if (candidates.length === 1) xbItem = candidates[0];
    }

    if (!xbItem) return false;

    const removeEl = xbItem.querySelector('.xb-wishlist-product__remove-product');
    if (!removeEl) return false;

    removeEl.dispatchEvent(
      new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
    );

    return true;
  }

  function signature(items) {
    // Include variantId so variant changes are treated as real updates
    return items.map((i) => `${i.handle}:${i.variantId || ''}`).join('|');
  }

  async function waitForStableItems(root, jobId, maxWaitMs, emptyGraceMs) {
    let lastSig = null;
    let stable = 0;
    const start = now();

    while (now() - start < maxWaitMs) {
      if (jobId !== currentJob) return null;

      const items = parseItems(root);
      const sig = signature(items);

      if (sig === lastSig) stable++;
      else stable = 0;

      lastSig = sig;

      const elapsed = now() - start;

      // If a pending removal is still present, wait a bit longer
      const hasPending = items.some((i) => pendingRemovals.has(i.handle));
      if (hasPending && elapsed < maxWaitMs - POLL_MS * 2) {
        await delay(POLL_MS);
        continue;
      }

      if (items.length > 0 && stable >= STABLE_TICKS) return items;

      if (items.length === 0 && stable >= STABLE_TICKS && elapsed >= emptyGraceMs) {
        // Accept empty only when XB shows its empty UI (or we basically timed out)
        if (xbHasEmptyState(root) || elapsed >= maxWaitMs - POLL_MS) return items;
      }

      await delay(POLL_MS);
    }

    if (jobId !== currentJob) return null;
    return parseItems(root);
  }

  async function fetchTextWithTimeout(url, signal, timeoutMs) {
    const c = new AbortController();
    const t = setTimeout(() => c.abort('timeout'), timeoutMs);

    const onAbort = () => c.abort('job_abort');
    if (signal) signal.addEventListener('abort', onAbort, { once: true });

    try {
      const res = await fetch(url, { credentials: 'same-origin', signal: c.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
      return await res.text();
    } finally {
      clearTimeout(t);
      if (signal) signal.removeEventListener('abort', onAbort);
    }
  }

  async function fetchCard(handle, variantId, signal) {
    const key = `${handle}|${variantId || ''}`;
    if (cardCache.has(key)) return cardCache.get(key);

    const url =
      `/products/${handle}?view=${encodeURIComponent(CARD_VIEW)}` +
      (variantId ? `&variant=${encodeURIComponent(variantId)}` : '');

    const html = await fetchTextWithTimeout(url, signal, FETCH_TIMEOUT_MS);
    cardCache.set(key, html);
    return html;
  }

  async function fetchAllCards(items, signal) {
    const out = new Array(items.length).fill(null);
    let idx = 0;

    const workersCount = Math.min(FETCH_CONCURRENCY, items.length);
    const workers = Array.from({ length: workersCount }, async () => {
      while (idx < items.length) {
        const i = idx++;
        const it = items[i];

        try {
          out[i] = await fetchCard(it.handle, it.variantId, signal);
        } catch (_) {
          out[i] = null;
        }

        if (signal && signal.aborted) return;
      }
    });

    await Promise.all(workers);
    return out.filter(Boolean);
  }

  function paintCards(cardHtmlList) {
    const frag = document.createDocumentFragment();
    let appended = 0;

    for (const html of cardHtmlList) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;

      const lis = tmp.querySelectorAll('li');
      if (lis.length) {
        lis.forEach((li) => {
          frag.appendChild(li);
          appended++;
        });
      } else {
        const li = document.createElement('li');
        li.className = 'grid__item';
        while (tmp.firstChild) li.appendChild(tmp.firstChild);
        frag.appendChild(li);
        appended++;
      }
    }

    if (!appended) return false;

    grid.innerHTML = '';
    grid.appendChild(frag);
    return true;
  }

  function refreshSparkB2B() {
    if (window.SparkB2B && typeof window.SparkB2B.refresh === 'function') {
      window.SparkB2B.refresh(grid);
    }
  }

  async function sync(mode) {
    if (!xbRoot) return;

    currentJob++;
    const myJob = currentJob;

    if (jobAbort) jobAbort.abort();
    jobAbort = new AbortController();

    setLoading(true, mode);

    try {
      const maxWaitMs = mode === 'soft' ? MAX_WAIT_ITEMS_SOFT_MS : MAX_WAIT_ITEMS_MS;

      const emptyGraceMs =
        mode === 'page'
          ? EMPTY_GRACE_PAGE_MS
          : mode === 'initial'
            ? EMPTY_GRACE_INITIAL_MS
            : EMPTY_GRACE_SOFT_MS;

      const items = await waitForStableItems(xbRoot, myJob, maxWaitMs, emptyGraceMs);
      if (items === null) return;

      // Confirm removals: if XB no longer has the handle, clear pendingRemovals
      const present = new Set(items.map((i) => i.handle));
      for (const h of Array.from(pendingRemovals)) {
        if (!present.has(h)) pendingRemovals.delete(h);
      }

      const visibleItems = items.filter((i) => !pendingRemovals.has(i.handle));

      if (!visibleItems.length) {
        // XB shows its own empty state; keep our grid empty
        grid.innerHTML = '';
        setLoading(false, mode);
        refreshSparkB2B();
        return;
      }

      const htmls = await fetchAllCards(visibleItems, jobAbort.signal);
      if (myJob !== currentJob) return;

      const ok = paintCards(htmls);
      if (!ok) paintStatus(ERROR_LI_HTML);

      setLoading(false, mode);
      refreshSparkB2B();
    } catch (_) {
      if (myJob !== currentJob) return;
      paintStatus(ERROR_LI_HTML);
      setLoading(false, mode);
    }
  }

  function requestSync(mode) {
    const prio = { soft: 0, initial: 1, page: 2 };
    if (!pendingMode || prio[mode] > prio[pendingMode]) pendingMode = mode;

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      const m = pendingMode || 'soft';
      pendingMode = null;
      sync(m);
    }, 150);
  }

  let removeBound = false;

  function bindRemoveHandlerOnce() {
    if (removeBound) return;
    removeBound = true;

    grid.addEventListener(
      'click',
      (e) => {
        const target = e.target instanceof Element ? e.target : null;
        const btn = target && target.closest ? target.closest('[data-wishlist-remove]') : null;
        if (!btn) return;

        e.preventDefault();
        e.stopPropagation();

        const handle = btn.dataset.handle;

        pendingRemovals.add(handle);
        const pendingTtl = setTimeout(() => pendingRemovals.delete(handle), 8000);

        const li = btn.closest('li.wishlist-grid__item');
        if (li) li.remove(); // optimistic UI

        setLoading(true, 'soft');

        suppressObserverUntil = now() + 2200;

        const ok = clickXbRemove(handle);
        if (!ok) {
          clearTimeout(pendingTtl);
          pendingRemovals.delete(handle); // rollback
          suppressObserverUntil = 0;
          setLoading(false, 'soft');
          requestSync('soft');
          return;
        }

        setTimeout(() => requestSync('soft'), 700);
      },
      true
    );
  }

  function attach(root) {
    xbRoot = root;

    requestSync('initial');
    bindRemoveHandlerOnce();

    const onPagination = (e) => {
      const t = e.target;
      if (!t || !t.closest) return;

      const btn = t.closest('.xb-pagination__button, .xb-pagination__page');
      if (!btn) return;

      if (btn.classList.contains('xb-pagination__button--disabled') || btn.disabled) return;
      if (btn.classList.contains('xb-pagination__page--active')) return;

      requestSync('page');
    };

    root.addEventListener('click', onPagination, true);
    root.addEventListener('pointerdown', onPagination, true);

    root.addEventListener(
      'change',
      (e) => {
        const t = e.target;
        if (t && t.matches && t.matches('select.xb-wishlist-product__select')) {
          requestSync('soft');
        }
      },
      true
    );

    const mo = new MutationObserver(() => {
      if (now() < suppressObserverUntil) return;
      requestSync('soft');
    });

    mo.observe(root, { childList: true, subtree: true });
  }

  function findAndAttach() {
    const root = document.querySelector('xb-wishlist-page');
    if (!root) return false;
    if (root === xbRoot) return true;

    attach(root);
    return true;
  }

  // --- Boot ---
  grid.innerHTML = LOADING_LI_HTML;
  grid.dataset.loading = '1';

  findAndAttach();

  const docMo = new MutationObserver(() => findAndAttach());
  docMo.observe(document.documentElement, { childList: true, subtree: true });

  setTimeout(findAndAttach, 300);
  setTimeout(findAndAttach, 1200);
})();
